import { Flex, Modal, Pagination, Spin, Switch, Table, Tag, Typography } from "antd"
import { Campaign } from "../interfaces/Campaign.interface"
import { CSSProperties, useEffect, useState } from "react"
import { APIService } from "../services/API.service"
import { useQuery } from "@tanstack/react-query"
import { NotificationPreview } from "./NotificationPreview/NotificationPreview"
import dayjs from "dayjs"
import { CampaignEvent } from "../interfaces/CampaignEvent.interface"
import { Legend, LineChart, Tooltip,ResponsiveContainer, XAxis, YAxis, Line } from 'recharts';
import { PushRole, useAuth } from "../hooks/useAuth"
import { PaginationOptions } from "../interfaces/PaginationOptions.interface"

export const CampaignDetail = (props: { applicationId: string, campaign: Campaign | null, isOpen: boolean, onDismiss: () => void }) => {
    const [ImageCSS, setImageCSS] = useState<CSSProperties>({
        objectFit: "contain"
    });
    const [cumulativeGraph, setCumulativeGraph] = useState<boolean>(false);
    const [logPaginationOptions, setLogPaginationOptions] = useState<PaginationOptions>({ limit: 100, offset: 0});
    const [currentLogPage, setCurrentLogPage] = useState<number>(0);
    const auth = useAuth();

    const [data, setData] = useState<any[]>([])

    const campaignDetailQuery = useQuery({ queryKey: ['campaigns', props.applicationId, 'detail', props.campaign?.id], queryFn: () => auth.api.getCampaignDetail(props.applicationId, props.campaign?.id ?? "unknown") })
    
    let campaign = campaignDetailQuery.data;

    const campaignLogsQuery = useQuery({ 
        enabled: auth.hasClaims(props.applicationId, PushRole.CanUseDeveloperTools),
        refetchInterval: 60000,
        queryKey: ['campaigns', props.applicationId, 'detail', props.campaign?.id, 'logs', 'limit', logPaginationOptions.limit, 'offset', logPaginationOptions.offset], 
        queryFn: ()=> auth.api.getCampaignLogs(props.applicationId, props.campaign?.id ?? "unknown", logPaginationOptions)
    })
    
    const campaignTimeSeriesQuery = useQuery({ 
        refetchInterval: 60000,
        queryKey: ['campaigns', props.applicationId, 'detail', props.campaign?.id, 'timeseries', 'cumulative', cumulativeGraph],
        queryFn: ()=> auth.api.getCampaignTimeSeries(props.applicationId, props.campaign?.id ?? "unknown", cumulativeGraph)
    })
    
    const onLogPaginationChange = (page:number, pageSize:number)=>{
        setCurrentLogPage(page);
        const newOffset = (page - 1) * pageSize;
        setLogPaginationOptions({ offset: newOffset, limit: pageSize })
    }
    
    useEffect(()=>{
        
        if(campaignTimeSeriesQuery.data && campaignTimeSeriesQuery.data.length > 0)
        {
            setData([{
                occuredAt: new Date(new Date(campaignTimeSeriesQuery.data[0].occuredAt).getTime() - 5000),
                queued: 0,
                sent: 0,
                send_error: 0,
                recv: 0,
                open: 0
            },...campaignTimeSeriesQuery.data.map(x=>{
                x.occuredAt = new Date(x.occuredAt);
                return x;
            })].map(x=>{
                (x as any)["t"] = dayjs(x.occuredAt).format("D MMM HH:mm");
                return x
            }));
        }

    }, [campaignTimeSeriesQuery.data])

    if (campaign) {
        const updateCumulative = (): void => {
            setCumulativeGraph(!cumulativeGraph)
        }

        let actions = campaign.pushMessage.actions;
        let openAction = actions.filter(x=>x.type == "open")[0];
        return (
            <>
                <Modal
                    open={props.isOpen}
                    onOk={() => props?.onDismiss()}
                    onCancel={() => props?.onDismiss()}
                    okText="OK"
                    cancelButtonProps={{ style: { display: 'none' } }}
                    width={`99vw`}
                >
                    <Flex gap={5} vertical>
                        <Typography.Title level={2}>{campaign?.name} <Typography.Text>(<Typography.Text copyable>{campaign?.id}</Typography.Text>)</Typography.Text></Typography.Title>
                        
                        <Flex gap={5} wrap={true} >{(campaign?.audiences ?? []).map(x => (<Tag key={x.id}>{`${x.name}`}</Tag>))}</Flex>

                        <Flex><Typography.Text>Title: </Typography.Text><Typography.Text>{campaign.pushMessage.title}</Typography.Text></Flex>
                        <Flex><Typography.Text>Message: </Typography.Text><Typography.Text>{campaign.pushMessage.message}</Typography.Text></Flex>

                        {actions && openAction && (<Flex><Typography.Text>Action(open): </Typography.Text><a href={openAction.url}>{openAction.url}</a></Flex>)}

                        <Flex><Typography.Text>View Cumulative: </Typography.Text><Switch onChange={updateCumulative} defaultValue={cumulativeGraph} /></Flex>

                        {Boolean(campaign.pushMessage.imageId || campaign.pushMessage.imageData) && (<Flex>
                            <img
                                style={ImageCSS}
                                src={`/api/v1/app/${props.applicationId}/campaign/${campaign.id}/image`}
                                height={200}
                                onError={() => { setImageCSS({ ...ImageCSS, display: 'none' }) }}
                            ></img>
                        </Flex>)}
                        
                    
                        { campaignTimeSeriesQuery.isLoading ? (<Flex align="center" justify="center" vertical><Flex align="center" justify="center" style={{ width: 700, height: 350}}><Spin /></Flex></Flex>) : (<><ResponsiveContainer width='100%' height={400}>
                         
                            <LineChart
                                width={700} height={350}
                                data={data ?? []}
                                margin={{
                                    top: 5,
                                    right: 10,
                                    left: 20,
                                    bottom: 0,
                                }}>

                                
                                <Line type="monotone" dataKey="queued" stroke="#C5D1EB" />
                                <Line type="monotone" dataKey="sent" stroke="#92AFD7" />
                                <Line type="monotone" dataKey="recv" stroke="#5A7684" />
                                <Line type="monotone" dataKey="open" stroke="#395B50" />
                                <Line type="monotone" dataKey="send_error" stroke="#FF0000" />
                                <XAxis dataKey="t"/>
                                <YAxis />
                                <Legend />
                                <Tooltip />
                            </LineChart>

                        </ResponsiveContainer>
                        </>
                        )}
                        {/* <NotificationPreview title={campaign.pushMessage.title} message={campaign.pushMessage.message} imageUrl={`/api/v1/app/${props.applicationId}/campaign/${campaign.id}/image`} type={"safari-mac"} deepLinkUrl={""}></NotificationPreview>
                    <NotificationPreview title={campaign.pushMessage.title} message={campaign.pushMessage.message} imageUrl={`/api/v1/app/${props.applicationId}/campaign/${campaign.id}/image`} type={"ios"} deepLinkUrl={""}></NotificationPreview>
                    <NotificationPreview title={campaign.pushMessage.title} message={campaign.pushMessage.message} imageUrl={`/api/v1/app/${props.applicationId}/campaign/${campaign.id}/image`} type={"chrome-mac"} deepLinkUrl={""}></NotificationPreview> */}
                    </Flex>
                    {auth.hasClaims(props.applicationId, PushRole.CanUseDeveloperTools) && campaignLogsQuery.data?.results && (
                        <>
                        <Table
                            rowKey={(row:CampaignEvent)=>`${row.occurredAt}-${row.event}-${row.deviceId}`}
                            columns={
                                [
                                        { title: 'Event', dataIndex: 'event', key: 'event', render: (eventName:string, event:CampaignEvent)=> `${eventName}${eventName == "send_error" ? ` ( ${event?.context?.reason} )` : ``}`},
                                        { title: 'Device', dataIndex: 'deviceId', key: 'deviceId', render: (deviceId:string)=> deviceId ? deviceId : "SYSTEM"},
                                        { title: 'Occured At', dataIndex: 'occuredAt', key: 'occuredAt', render: (value: string) => dayjs(value).format('D MMMM YYYY h:mm A') },                                    
                                    ]
                            }
                            loading={campaignLogsQuery.isFetching}
                            dataSource={campaignLogsQuery.data.results ?? []}
                            pagination={false}
                            >
                        
                        </Table>
                        <Pagination current={currentLogPage} onChange={onLogPaginationChange} total={campaignLogsQuery.data?.total} pageSize={logPaginationOptions.limit} pageSizeOptions={[10,20,50,100]} />
                        </>
                        )
                    }
                </Modal>
            </>
        )
    }
    return (<></>)




}